import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirTaskCategoryType } from '@econtractor/types';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import CreatableNameSelect from 'components/forms/CreatableNameSelect';
import * as apiSettings from 'api/apiSettings';
import { createTaskCategory } from 'api/apiTask';
import type { TaskCategoryOption } from './types';

interface TaskCategorySelectProps {
    value?: number | null;
    onChange: (option: TaskCategoryOption | null) => void;
    disabled?: boolean;
    /** false забороняє створювати нову категорію з цього поля. */
    allowCreate?: boolean;
    inputId?: string;
}

const CATEGORY_NAME_MAX_LENGTH = 50;

const TaskCategorySelect = ({
    value,
    onChange,
    disabled = false,
    allowCreate = true,
    inputId,
}: TaskCategorySelectProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { localSettings, dispatchLocalSettings } = useSettings();
    const { showToast } = useToast();
    const [creating, setCreating] = useState(false);

    const options = useMemo<TaskCategoryOption[]>(() => {
        const items = (localSettings?.DIRS?.TaskCategory || []) as DirTaskCategoryType[];
        return items
            .filter((item) => !item.hidden || item.ID === value)
            .map((item) => ({ value: item.ID, label: item.categoryName }));
    }, [localSettings?.DIRS, value]);

    const handleCreate = async (inputValue: string): Promise<boolean> => {
        const name = String(inputValue || '').trim();
        if (!name || creating) return false;
        setCreating(true);
        try {
            const res = await createTaskCategory(name);
            if (res?.stat && res.id) {
                const settings = await apiSettings.getSettings();
                if (settings) dispatchLocalSettings({ type: 'SET', settings });
                onChange({ value: res.id, label: res.name || name });
                return true;
            }
            if (res?.errCode === 'DUPLICATE_NAME') {
                const existing = options.find((opt) => opt.label.trim().toLowerCase() === name.toLowerCase());
                if (existing) {
                    onChange(existing);
                    return true;
                }
                showToast(t('dirs.messDuplicate'), 'danger');
                return false;
            }
            if (res?.errCode === 'EMPTY_NAME') {
                showToast(t('dirs.messEmpty'), 'danger');
            } else if (res?.errCode === 'NAME_TOO_LONG') {
                showToast(t('dirs.messTooLong'), 'danger');
            } else {
                showToast(t('common:messError'), 'danger');
            }
            return false;
        } finally {
            setCreating(false);
        }
    };

    return (
        <CreatableNameSelect
            inputId={inputId}
            options={options}
            value={value}
            disabled={disabled}
            allowCreate={allowCreate}
            placeholder={t('fields.category')}
            addLabel={t('dirs.btnAdd')}
            requiredMessage={t('dirs.messEmpty')}
            maxLength={CATEGORY_NAME_MAX_LENGTH}
            formatCreateLabel={(input) => t('createOption', { name: input })}
            onChange={onChange}
            onCreate={handleCreate}
        />
    );
};

export default TaskCategorySelect;
